// App.js
import { React, useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, NavLink, Navigate, useLocation } from 'react-router-dom';
import ImageUpload from './ImageUpload';
import JobListingsUpload from './JobListingsUpload';
import JobsRevenueUpload from './JobsRevenueUpload';
import RepairAnalysisUpload from './RepairAnalysisUpload';
import FinanceReportUpload from './FinanceReportUpload';
import SapReportUpload from './SapReportUpload';
import ReportsPage from './ReportsPage';
import ReviewDocsPage from './ReviewDocsPage';
import LoginPage from './LoginPage';
import NoAuthPage from './NoAuthPage';
import ProtectedRoute from './ProtectedRoute';
import './App.css';
import logo from './maxresdefault.jpg'; // Make sure to import your logo image
import ReviewExportPage from './ReviewExportPage';
import ReviewSupplierMappingPage from './ReviewSupplierMappingPage';
import SuppliersPage from './SuppliersPage';
import Dashboard from './Dashboard';
import BudgetPage from './BudgetPage';
import UserMenu from './UserMenu';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useAuthSession } from './AuthSessionContext';



function Header() {
  const location = useLocation();
  const { user, roles: userRoles } = useAuthSession();
  const isAuthenticated = Boolean(user);
  const userEmail = user?.unique_name || user?.preferred_username || user?.email;
  const getNavLinkClassName = ({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`;
  const canUploadDocs = userRoles.includes('uploader') || userRoles.includes('reviewer') || userRoles.includes('admin');
  const canUploadJobListings = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canUploadJobsRevenue = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canUploadRepairAnalysis = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canUploadFinanceReports = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canUploadSapReports = userRoles.includes('reviewer') || userRoles.includes('admin');
  const showUploadMenu = canUploadDocs || canUploadJobListings || canUploadJobsRevenue || canUploadRepairAnalysis || canUploadFinanceReports || canUploadSapReports;
  const uploadPaths = ['/upload-docs', '/upload-job-listings', '/upload-jobs-revenue', '/upload-repair-analysis', '/upload-finance', '/upload-sap'];
  const isUploadRouteActive = uploadPaths.some((path) => location.pathname.startsWith(path));
  const canViewSuppliersList = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canViewSuppliersMapping = userRoles.includes('reviewer') || userRoles.includes('admin');
  const canViewReports = userRoles.includes('reviewer') || userRoles.includes('admin');
  const showSuppliersMenu = canViewSuppliersList || canViewSuppliersMapping;
  const suppliersPaths = ['/suppliers-list', '/suppliers-mapping'];
  const isSuppliersRouteActive = suppliersPaths.some((path) => location.pathname.startsWith(path));
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const closeMenuTimeoutRef = useRef(null);
  const [suppliersMenuOpen, setSuppliersMenuOpen] = useState(false);
  const suppliersCloseMenuTimeoutRef = useRef(null);

  const clearCloseMenuTimeout = () => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const openUploadMenu = () => {
    clearCloseMenuTimeout();
    setUploadMenuOpen(true);
  };

  const closeUploadMenuDelayed = () => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setUploadMenuOpen(false);
      closeMenuTimeoutRef.current = null;
    }, 150);
  };

  const closeUploadMenuImmediate = () => {
    clearCloseMenuTimeout();
    setUploadMenuOpen(false);
  };

  const toggleUploadMenu = () => {
    clearCloseMenuTimeout();
    setUploadMenuOpen((prev) => !prev);
  };

  const handleUploadMenuLinkClick = () => {
    setTimeout(() => {
      closeUploadMenuImmediate();
    }, 0);
  };

  const clearSuppliersCloseMenuTimeout = () => {
    if (suppliersCloseMenuTimeoutRef.current) {
      clearTimeout(suppliersCloseMenuTimeoutRef.current);
      suppliersCloseMenuTimeoutRef.current = null;
    }
  };

  const openSuppliersMenu = () => {
    clearSuppliersCloseMenuTimeout();
    setSuppliersMenuOpen(true);
  };

  const closeSuppliersMenuDelayed = () => {
    clearSuppliersCloseMenuTimeout();
    suppliersCloseMenuTimeoutRef.current = setTimeout(() => {
      setSuppliersMenuOpen(false);
      suppliersCloseMenuTimeoutRef.current = null;
    }, 150);
  };

  const closeSuppliersMenuImmediate = () => {
    clearSuppliersCloseMenuTimeout();
    setSuppliersMenuOpen(false);
  };

  const toggleSuppliersMenu = () => {
    clearSuppliersCloseMenuTimeout();
    setSuppliersMenuOpen((prev) => !prev);
  };

  const handleSuppliersMenuLinkClick = () => {
    setTimeout(() => {
      closeSuppliersMenuImmediate();
    }, 0);
  };
  

  useEffect(() => {
    return () => {
      clearCloseMenuTimeout();
      clearSuppliersCloseMenuTimeout();
    };
  }, []);

  
  return (
    <header className="App-header">
      <div className="logo-title">
        <NavLink to="/" className="logo-link">
          <img src={logo} className="logo" alt="Logo" />
          <h1 className="title">HaloVault</h1>
        </NavLink>
      </div>

      <nav>
        {!isAuthenticated ? (
          <NavLink to="/login" className={getNavLinkClassName}>
            Login
          </NavLink>
        ) : (
          <>
            {(userRoles.includes('reviewer') || userRoles.includes('admin') || userRoles.includes('reader')) && (
              <NavLink to="/dashboard" className={getNavLinkClassName}>
                Dashboard
              </NavLink>
            )}
            {(userRoles.includes('reviewer') || userRoles.includes('admin')) && (
              <NavLink to="/review" className={getNavLinkClassName}>
                Review Docs
              </NavLink>
            )}
            {canViewReports && (
              <NavLink to="/reports" className={getNavLinkClassName}>
                Reports
              </NavLink>
            )}
            {showUploadMenu && (
              <div
                className={`nav-dropdown${uploadMenuOpen ? ' nav-dropdown--open' : ''}`}
                onMouseEnter={openUploadMenu}
                onMouseLeave={closeUploadMenuDelayed}
              >
                <button
                  type="button"
                  className={`nav-link nav-dropdown__trigger${isUploadRouteActive ? ' nav-link--active' : ''}`}
                  onMouseEnter={openUploadMenu}
                  onClick={toggleUploadMenu}
                  onFocus={openUploadMenu}
                  aria-haspopup="true"
                  aria-expanded={uploadMenuOpen}
                >
                  Upload ▾
                </button>
                <div
                  className="nav-dropdown__menu"
                  role="menu"
                  onMouseEnter={openUploadMenu}
                  onMouseLeave={closeUploadMenuDelayed}
                >
                  {canUploadDocs && (
                    <NavLink
                      to="/upload-docs"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      Docs
                    </NavLink>
                  )}
                  {canUploadJobListings && (
                    <NavLink
                      to="/upload-job-listings"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      Jobs Listing
                    </NavLink>
                  )}
                  {canUploadJobsRevenue && (
                    <NavLink
                      to="/upload-jobs-revenue"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      Jobs Revenue
                    </NavLink>
                  )}
                  {canUploadRepairAnalysis && (
                    <NavLink
                      to="/upload-repair-analysis"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      Repair Analysis
                    </NavLink>
                  )}
                  {canUploadFinanceReports && (
                    <NavLink
                      to="/upload-finance"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      Finance Report
                    </NavLink>
                  )}
                  {canUploadSapReports && (
                    <NavLink
                      to="/upload-sap"
                      className={getNavLinkClassName}
                      onClick={handleUploadMenuLinkClick}
                      role="menuitem"
                    >
                      SAP Report
                    </NavLink>
                  )}
                </div>
              </div>
            )}
            {/* {(userRoles.includes('reviewer')   || userRoles.includes('admin')) && (
              <NavLink to="/export" className={getNavLinkClassName}>
                Export Docs
              </NavLink>
            )} */}
            {showSuppliersMenu && (
              <div
                className={`nav-dropdown${suppliersMenuOpen ? ' nav-dropdown--open' : ''}`}
                onMouseEnter={openSuppliersMenu}
                onMouseLeave={closeSuppliersMenuDelayed}
              >
                <button
                  type="button"
                  className={`nav-link nav-dropdown__trigger${isSuppliersRouteActive ? ' nav-link--active' : ''}`}
                  onMouseEnter={openSuppliersMenu}
                  onClick={toggleSuppliersMenu}
                  onFocus={openSuppliersMenu}
                  aria-haspopup="true"
                  aria-expanded={suppliersMenuOpen}
                >
                  Suppliers ▾
                </button>
                <div
                  className="nav-dropdown__menu"
                  role="menu"
                  onMouseEnter={openSuppliersMenu}
                  onMouseLeave={closeSuppliersMenuDelayed}
                >
                  {canViewSuppliersList && (
                    <NavLink
                      to="/suppliers-list"
                      className={getNavLinkClassName}
                      onClick={handleSuppliersMenuLinkClick}
                      role="menuitem"
                    >
                      List
                    </NavLink>
                  )}
                  {canViewSuppliersMapping && (
                    <NavLink
                      to="/suppliers-mapping"
                      className={getNavLinkClassName}
                      onClick={handleSuppliersMenuLinkClick}
                      role="menuitem"
                    >
                      Mapping
                    </NavLink>
                  )}
                </div>
              </div>
            )}
            {(userRoles.includes('admin') || userRoles.includes('reviewer')) && (
              <NavLink to="/budget" className={getNavLinkClassName}>
                Budget
              </NavLink>
            )}
            {userEmail && <span><UserMenu userEmail={userEmail} /></span>}
          </>
        )}
      </nav>
    </header>
  );
}

const HomeRedirect = () => {
  const { user, roles: userRoles, loading } = useAuthSession();
  const isAuthenticated = Boolean(user);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (
    userRoles.includes('uploader') &&
    !userRoles.some((role) => role === 'reviewer' || role === 'admin')
  ) {
    return <Navigate to="/upload-docs" replace />;
  }

  if (userRoles.includes('reviewer') || userRoles.includes('admin') || userRoles.includes('reader')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (userRoles.length === 0) {
    return <Navigate to="/no-auth" replace />;
  }

  return <Navigate to="/review" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer", "reader"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/budget"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <BudgetPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-docs"
        element={
          <ProtectedRoute allowedRoles={["admin", "uploader", "reviewer"]}>
            <ImageUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-job-listings"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <JobListingsUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-jobs-revenue"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <JobsRevenueUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-repair-analysis"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <RepairAnalysisUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-finance"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <FinanceReportUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upload-sap"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <SapReportUpload />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/review"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <ReviewDocsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/export"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <ReviewExportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/suppliers-list"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <SuppliersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/suppliers-mapping"
        element={
          <ProtectedRoute allowedRoles={["admin", "reviewer"]}>
            <ReviewSupplierMappingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/no-auth"
        element={
          <NoAuthPage />
        }
      />

      <Route 
        path="/login"
        element={
          <LoginPage />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />

    </Routes>
  );
}

function App() {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      setToast({
        open: true,
        message: detail.message || 'You are not allowed to access this resource.',
        severity: detail.severity || 'error',
      });
    };

    window.addEventListener('hv-toast', handler);
    return () => {
      window.removeEventListener('hv-toast', handler);
    };
  }, []);

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <Router>
      <div className="App">
        <Header />
        <main>
          <AppRoutes />
        </main>
        <Snackbar open={toast.open} autoHideDuration={5000} onClose={closeToast}>
          <Alert onClose={closeToast} severity={toast.severity} sx={{ minWidth: 240 }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </Router>
  );
}

export default App;
